import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

import { UserService } from '../../_services/data-services/user.service';

import { UserPropertyResultDto } from '../../_models/user-property-result-dto';
import { BaseResidentParameterDto } from '../../_models/base-resident-parameter-dto';
import { ActionItemConfigResultDto } from '../../_models/action-item-config-result-dto';
import { MeetingResultDto } from './models/meeting-result-dto';
import { MeetingHubWidgetService } from './meeting-hub-widget.service';
import { openExternalUrl } from '../../_helpers/open-external-url';

import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-meeting-hub-widget',
  templateUrl: './meeting-hub-widget.component.html',
  styleUrls: ['./meeting-hub-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MeetingHubWidgetComponent implements OnInit, OnDestroy {
  @Input() selectedProperty: UserPropertyResultDto;

  isLoading = true;
  showWidget = false;
  hasToken = false;
  nextMeeting: MeetingResultDto | null = null;

  private readonly destroy$ = new Subject<void>();
  private propertyChangeSubscription: Subscription;

  constructor(
    private userService: UserService,
    private meetingHubWidgetService: MeetingHubWidgetService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadData();

    this.propertyChangeSubscription = RxjsUtils.subscribeToSubject(
      this.userService.getSubjectPropertySelectionChanged(),
      () => {
        this.selectedProperty = this.userService.getSelectedProperty();
        this.loadData();
      }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.propertyChangeSubscription) {
      this.propertyChangeSubscription.unsubscribe();
    }
  }

  getBadgeText(): string {
    if (!this.nextMeeting?.scheduledStart) return 'Upcoming Meeting';
    const now = new Date();
    const start = new Date(this.nextMeeting.scheduledStart);
    const diffMs = start.getTime() - now.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays <= 7 && diffDays > 3) {
      const day = start.toLocaleDateString('en-US', { weekday: 'long' });
      const hour = start.getHours();
      const minutes = start.getMinutes();
      const period = hour >= 12 ? 'p' : 'a';
      const displayHour = hour % 12 || 12;
      const timeStr =
        minutes > 0
          ? `${displayHour}:${String(minutes).padStart(2, '0')}${period}`
          : `${displayHour}${period}`;
      return `Next ${day} at ${timeStr}`;
    }

    return 'Upcoming Meeting';
  }

  onGoToMeetingHub(): void {
    // Direct router navigation. The previous path through
    // `HelperService.navigateToRoute(info)` re-emitted a property-selection
    // subject — which the dashboard's 15+ property-change subscribers all
    // reacted to — producing a visible re-render flicker before the route
    // actually changed. The widget already shows data for the currently
    // selected property, so there's no property to "select" here.
    this.router.navigate(['/community/meeting-hub']);
  }

  getFormattedDate(): string {
    if (!this.nextMeeting?.scheduledStart) return '';
    const date = new Date(this.nextMeeting.scheduledStart);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  getFormattedTime(): string {
    if (!this.nextMeeting?.scheduledStart) return '';
    const start = new Date(this.nextMeeting.scheduledStart);
    const end = this.nextMeeting.duration
      ? new Date(start.getTime() + this.nextMeeting.duration * 60000)
      : null;

    const formatTime = (d: Date): string =>
      d
        .toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })
        .toLowerCase();

    const startTime = formatTime(start);
    return end ? `${startTime} - ${formatTime(end)}` : startTime;
  }

  isOpenMeeting(): boolean {
    return this.nextMeeting?.accessType === 'Open';
  }

  getLocationName(): string {
    if (!this.isOpenMeeting()) {
      if (this.nextMeeting?.inPersonMeeting) return 'In Person';
      if (this.nextMeeting?.onlineMeeting?.joinUrl) return 'Online Via Zoom';
      return '';
    }
    if (this.nextMeeting?.inPersonMeeting?.location) {
      return this.nextMeeting.inPersonMeeting.location;
    }
    if (this.nextMeeting?.onlineMeeting?.joinUrl) {
      return 'Online Via Zoom';
    }
    return '';
  }

  getLocationAddress(): string {
    if (!this.isOpenMeeting()) return '';
    const meeting = this.nextMeeting?.inPersonMeeting;
    if (!meeting?.streetAddress1) return '';
    return meeting.streetAddress1;
  }

  getLocationCityStateZip(): string {
    if (!this.isOpenMeeting()) return '';
    const meeting = this.nextMeeting?.inPersonMeeting;
    if (!meeting) return '';
    const cityState = [meeting.city, meeting.state].filter(Boolean).join(', ');
    const parts = [cityState, meeting.zip].filter(Boolean);
    return parts.join(' ');
  }

  showJoinMeetingLink(): boolean {
    return (
      this.nextMeeting?.accessType === 'Open' &&
      !!this.nextMeeting?.onlineMeeting?.joinUrl
    );
  }

  onJoinMeeting(): void {
    openExternalUrl(this.nextMeeting?.onlineMeeting?.joinUrl);
  }

  private loadData(): void {
    if (!this.selectedProperty) {
      this.selectedProperty = this.userService.getSelectedProperty();
    }

    if (this.selectedProperty) {
      this.loadUserActions();
    }
  }

  private loadUserActions(): void {
    this.isLoading = true;
    const param = new BaseResidentParameterDto(
      this.selectedProperty.customerId,
      this.selectedProperty.communityId
    );

    this.userService
      .getUserActionItems(param)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (actions: ActionItemConfigResultDto[]) => {
          const meetingHubAction = actions?.find(
            (action) => action.item?.toLowerCase() === 'meeting-hub'
          );
          this.showWidget = meetingHubAction?.enable ?? false;

          if (this.showWidget) {
            this.loadNextMeeting();
          } else {
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.showWidget = false;
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private loadNextMeeting(): void {
    if (!this.meetingHubWidgetService.hasToken()) {
      this.meetingHubWidgetService
        .authenticate()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (success) => {
            this.hasToken = success;
            if (success) {
              this.fetchMeetings();
            } else {
              this.isLoading = false;
              this.cdr.markForCheck();
            }
          },
          error: () => {
            this.hasToken = false;
            this.isLoading = false;
            this.cdr.markForCheck();
          }
        });
      return;
    }
    this.hasToken = true;
    this.fetchMeetings();
  }

  private fetchMeetings(): void {
    this.meetingHubWidgetService
      .getUpcomingMeetings(this.selectedProperty.communityId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (meetings: MeetingResultDto[]) => {
          const now = new Date();
          const upcoming = meetings
            ?.filter((m) => new Date(m.scheduledStart) > now)
            .sort(
              (a, b) =>
                new Date(a.scheduledStart).getTime() -
                new Date(b.scheduledStart).getTime()
            );
          this.nextMeeting = upcoming?.length > 0 ? upcoming[0] : null;
        },
        error: () => {
          this.nextMeeting = null;
        }
      });
  }
}
